import { useState } from 'react';
import { useIntl } from 'react-intl';
import { ButtonUI, CustomThemeProvider, Popover, Tag } from 'sonata-react-components';
import { useTheme } from 'styled-components';

import { formatFileSize } from '../../../export/fileSize.helper';

import './listDownloadMenu.scss';
import '@/components/common/ContextualPanel/contextualPanel.scss';

import type { ExportFormat } from '../../../export/export.types';
import type { ExportScope } from '../../../export/listExport.helper';
import type { ExportSizes } from '../../../export/useExportSizes';

type Props = {
  /** Items of each scope. "Only selected items" is offered only when something is selected. */
  counts: Record<ExportScope, number>;
  /** Disables the button (e.g. the list is empty or a file is being generated). */
  disabled?: boolean;
  id: string;
  /** Lets the owner calculate the file sizes only while the menu is open. */
  onOpenChange?: (open: boolean) => void;
  /** Called with the chosen scope and file format; the menu closes right before. */
  onSelect: (format: ExportFormat, scope: ExportScope) => void;
  /** Estimated file size in bytes per scope and format; a missing one is left out of the button. */
  sizes?: ExportSizes;
};

// The design labels the Excel button ".xls" although the generated file is .xlsx
const FORMAT_BUTTONS: { format: ExportFormat; icon: string; label: string }[] = [
  { format: 'csv', icon: 'fa-solid fa-file-csv', label: '.CSV' },
  { format: 'excel', icon: 'fa-solid fa-download', label: '.xls' }
];

/** Icon button that opens a popover to export the selected or all the items of a list as CSV or Excel. */
export function ListDownloadMenu({
  counts,
  disabled = false,
  id,
  onOpenChange,
  onSelect,
  sizes = {}
}: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const { themeContrast } = useTheme() as { themeContrast: 'light' | 'dark' | 'darkAI' };
  const [isOpen, setIsOpen] = useState(false);

  const scopes: { scope: ExportScope; title: string }[] = [
    ...(counts.selected > 0
      ? [
          {
            scope: 'selected' as const,
            title: formatMessage({
              defaultMessage: 'Only selected items',
              id: 'multiFlightListsMap.actionButtons.download.menu.selected'
            })
          }
        ]
      : []),
    {
      scope: 'all',
      title: formatMessage({
        defaultMessage: 'All items',
        id: 'multiFlightListsMap.actionButtons.download.menu.all'
      })
    }
  ];

  const changeOpen = (open: boolean) => {
    setIsOpen(open);
    onOpenChange?.(open);
  };

  const handleSelect = (format: ExportFormat, scope: ExportScope) => {
    changeOpen(false);
    onSelect(format, scope);
  };

  return (
    <div className="listDownloadMenu">
      <Popover onOpenChange={changeOpen} open={isOpen} placement="bottom-start">
        <Popover.Trigger asChild>
          <ButtonUI
            disabled={disabled}
            icon="fa-regular fa-arrow-down-to-line"
            id={`${id}-trigger`}
            onClick={() => changeOpen(!isOpen)}
            size="ultraSmall"
            title={formatMessage({
              defaultMessage: 'Download',
              id: 'multiFlightListsMap.actionButtons.download.button'
            })}
            type="secondary"
          />
        </Popover.Trigger>
        <Popover.Content id={`${id}-menu`} style={{ zIndex: 1000 }}>
          <CustomThemeProvider themeContrast={themeContrast}>
            <div className="contextualPanel listDownloadMenu__panel">
              <span className="listDownloadMenu__title">
                {formatMessage({
                  defaultMessage: 'Export your list',
                  id: 'multiFlightListsMap.actionButtons.download.menu.title'
                })}
              </span>
              {scopes.map(({ scope, title }) => (
                <div
                  aria-labelledby={`${id}-${scope}-title`}
                  className="listDownloadMenu__block"
                  key={scope}
                  role="group"
                >
                  <div className="listDownloadMenu__blockHeader">
                    <span className="listDownloadMenu__blockTitle" id={`${id}-${scope}-title`}>
                      {title}
                    </span>
                    <Tag className="listDownloadMenu__count" id={`${id}-${scope}-count`} rounded status="category" tiny>
                      {counts[scope]}
                    </Tag>
                  </div>
                  <div className="listDownloadMenu__formats">
                    {FORMAT_BUTTONS.map(({ format, icon, label }) => {
                      const size = sizes[scope]?.[format];
                      return (
                        <button
                          className="listDownloadMenu__format"
                          id={`${id}-${scope}-${format}`}
                          key={format}
                          onClick={() => handleSelect(format, scope)}
                          type="button"
                        >
                          <i aria-hidden="true" className={`${icon} listDownloadMenu__formatIcon`} />
                          <span className="listDownloadMenu__formatLabel">{label}</span>
                          {size !== undefined && (
                            <span className="listDownloadMenu__formatSize">({formatFileSize(size)})</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </CustomThemeProvider>
        </Popover.Content>
      </Popover>
    </div>
  );
}
